import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/client';
import withStyles from 'isomorphic-style-loader/withStyles';
import cx from 'classnames';
import { useIntl } from 'react-intl';

import Loader from '../../../components/Loader/Loader';
import Link from '../../../components/Link/Link';
import CommonFormComponent from '../../../components/CommonField/CommonFormComponent';
import messages from '../../../locale/jsonMessages';
import {
  getActiveLanguages,
  getPushNotificationTemplatesByType,
  updatePushNotificationTemplate
} from '../../../lib/graphql';
import validate, { TITLE_MAX_LENGTH, MESSAGE_MAX_LENGTH } from './validate';
import { typeToLabel, slugToType } from '../pushNotificationTemplates/pushNotificationTemplateSlug';
import showToaster from '../../../helpers/toasterMessages/showToaster';
import bt from '../../../components/commonStyle.css';

function PushNotificationTemplateEdit(props) {
  const { type: typeParam } = props;
  const intl = useIntl();
  const type = slugToType(typeParam) || typeParam || '';
  const effectiveType = type ? decodeURIComponent(type) : '';

  const [byLocale, setByLocale] = useState({});
  const [saving, setSaving] = useState(false);
  const [errorsByLocale, setErrorsByLocale] = useState({});

  const { data: templatesData, loading: templatesLoading } = useQuery(getPushNotificationTemplatesByType, {
    variables: { type: effectiveType },
    skip: !effectiveType,
    fetchPolicy: 'network-only',
    ssr: false
  });

  const { data: languagesData, loading: languagesLoading } = useQuery(getActiveLanguages, {
    variables: { appType: 'android_app' },
    fetchPolicy: 'network-only',
    ssr: false
  });

  const [mutateUpdatePushNotificationTemplate] = useMutation(updatePushNotificationTemplate);

  const languages = languagesData?.getActiveLanguages?.results ?? [];
  const templates = templatesData?.getPushNotificationTemplatesByType ?? [];

  const localeSections = useMemo(
    () => languages.map((language) => ({
      key: language.languageCode,
      label: `${language.language} (${language.languageCode})`,
      localeValue: language.languageCode
    })),
    [languages]
  );

  useEffect(() => {
    if (!effectiveType || localeSections.length === 0) return;

    const next = {};
    localeSections.forEach(({ key, localeValue }) => {
      const row = templates.find((template) => template.locale === localeValue);
      next[key] = {
        title: row?.title ?? '',
        messageBody: row?.messageBody ?? ''
      };
    });

    setByLocale(next);
  }, [effectiveType, templates, localeSections]);

  const updateLocale = (localeKey, field, value) => {
    // const maxLen = field === 'title' ? TITLE_MAX_LENGTH : MESSAGE_MAX_LENGTH;
    // const trimmedValue = typeof value === 'string' ? value.slice(0, maxLen) : value;

    setByLocale((prev) => ({
      ...prev,
      [localeKey]: {
        ...(prev[localeKey] || { title: '', messageBody: '' }),
        [field]: value
      }
    }));

    setErrorsByLocale((prev) => {
      if (!prev[localeKey]?.[field]) return prev;
      const next = { ...prev };
      next[localeKey] = { ...next[localeKey], [field]: undefined };
      if (!next[localeKey].title && !next[localeKey].messageBody) {
        delete next[localeKey];
      }
      return next;
    });
  };

  const handleSave = async () => {
    const validationErrors = validate(byLocale, localeSections);
    setErrorsByLocale(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setSaving(true);
    try {
      const updatedData = await Promise.all(
        localeSections.map(({ key, localeValue }) => {
          const localeData = byLocale[key] || {};
          return mutateUpdatePushNotificationTemplate({
            variables: {
              type: effectiveType,
              locale: localeValue,
              title: localeData.title || '',
              messageBody: localeData.messageBody || ''
            }
          });
        })
      );

      if (updatedData[0].data.updatePushNotificationTemplate.status == 200) {
        return await showToaster({ messageId: 'pushNotificationUpdateSuccess', toasterType: 'success' })
      } else {
        return await showToaster({ messageId: 'pushNotificationUpdateFailed', toasterType: 'error' })
      }
    } catch (error) {
      console.error('Failed to update push notification template', error);
      return await showToaster({ messageId: 'pushNotificationUpdateFailed', toasterType: 'error' })
    } finally {
      setSaving(false);
    }
  };

  if (templatesLoading || languagesLoading) {
    return <Loader type="text" />;
  }

  return (
    <div >
      <h1 className={'siteAdminHeading'}>{intl.formatMessage(messages.pushNotificationTemplateEditTitle)}: {typeToLabel(effectiveType)}</h1>

      {localeSections.map(({ key, label }, index) => {
        const localeData = byLocale[key] || {};
        const title = localeData.title || '';
        const messageBody = localeData.messageBody || '';
        const err = errorsByLocale[key] || {};
        const isLast = index === localeSections.length - 1;

        return (
          <div key={key} >
            <p className={cx(bt.commonContentText, bt.spaceBottom2, bt.fontWeightBold)}>{label}</p>
            <div className='twoColumnGrid'>
              <CommonFormComponent
                input={{
                  value: title,
                  onChange: (event) => updateLocale(key, 'title', event.target.value)
                }}
                meta={{
                  touched: Boolean(err.title),
                  error: err.title ? { id: err.title, defaultMessage: err.title } : null
                }}
                toolTipText={intl.formatMessage(messages.pushNotificationTitleContent)}
                type="text"
                labelText="Title"
                isRequiredInput
                inputClass='commonInputField'
              />

              <CommonFormComponent
                input={{
                  value: messageBody,
                  onChange: (event) => updateLocale(key, 'messageBody', event.target.value)
                }}
                meta={{
                  touched: Boolean(err.messageBody),
                  error: err.messageBody ? { id: err.messageBody, defaultMessage: err.messageBody } : null
                }}
                labelText="Message"
                isRequiredInput
                toolTipText={intl.formatMessage(messages.pushNotificationMessageContent)}
                inputClass='commonTextAreaField'
                componentClass="textarea"
              />
            </div>
            {!isLast && <div className={cx(bt.dividerBorder, bt.spaceBottom3, bt.spaceTop3)} />}
          </div>
        );
      })}

      {localeSections.length > 0 && (
        <div className={cx(bt.displayFlex, bt.gap16, bt.flexWrap, bt.justifyContentFlexEnd, bt.spaceTop4)}>
          <Link to="/siteadmin/push-notification-templates" className={bt.btnPrimaryBorder}>
            {intl.formatMessage(messages.cancel)}
          </Link>
          <Loader
            type="button"
            buttonType="submit"
            handleClick={handleSave}
            show={saving}
            label={intl.formatMessage(messages.save)}
            className={cx(bt.btnPrimary, 'loaderButtonFlex')}
          />
        </div>
      )}
    </div>
  );
}

PushNotificationTemplateEdit.propTypes = {
  type: PropTypes.string,
  locale: PropTypes.string
};

PushNotificationTemplateEdit.defaultProps = {
  type: '',
  locale: ''
};

export default withStyles(bt)(PushNotificationTemplateEdit);
